import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ApolloCache } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  PortalPermissions,
  useFeatureSubscription_GetSeatQuantityLazyQuery,
  User_GetPortalAccountUsersDocument,
  User_GetPortalAccountUsersQuery,
  User_InvitePortalUserInput,
  useUser_GetPortalAccountUsersQuery,
  useUser_InvitePortalUserMutation,
} from 'queries';
import * as yup from 'yup';

import FormInputErrorMessage from 'common/form-input-error-message';
import FormPhoneField from 'common/form-phone-field';
import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
  },
  inputField: {
    width: '100%',
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
  warning: {
    display: 'block',
    color: 'red',
    fontSize: '.65rem',
    lineHeight: '1.3',
  },
}));

const schema = yup.object().shape({
  firstName: yup.string().required('Please enter a first name'),
  lastName: yup.string().required('Please enter a last name'),
  email: yup.string().required('Please enter an email').email(),
  phone: yup.string().required('Please enter a phone number'),
});

export default function InviteUserForm({ onExitClick }: Props) {
  const classes = useStyles();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<User_InvitePortalUserInput>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
  });
  const { portalId } = useParams<Record<string, string>>();
  const [seatQuantity, setSeatQuantity] = useState(0);

  const [loadSeatQuantity] = useFeatureSubscription_GetSeatQuantityLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!data) {
        showAlert({
          message: 'Error getting seat quantity',
          severity: NotificationSeverity.ERROR,
        });
        onExitClick();
        return;
      }

      setSeatQuantity(data.data);
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting seat quantity: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: usersData } = useUser_GetPortalAccountUsersQuery({
    variables: {
      input: {
        portalId,
        includeAccountDisabledUsers: false,
        includeClientUsers: false,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal account users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const activeUsers = usersData ? usersData.data : [];

  const readPortalAccountUsersQuery = (
    store: ApolloCache<User_GetPortalAccountUsersQuery>,
  ): User_GetPortalAccountUsersQuery | null => {
    return store.readQuery<User_GetPortalAccountUsersQuery>({
      query: User_GetPortalAccountUsersDocument,
      variables: {
        portalId,
      },
    });
  };

  const writePortalAccountUsersQuery = (
    store: ApolloCache<User_GetPortalAccountUsersQuery>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    users: any,
  ) => {
    store.writeQuery<User_GetPortalAccountUsersQuery>({
      query: User_GetPortalAccountUsersDocument,
      variables: {
        portalId,
      },
      data: {
        data: users,
      },
    });
  };

  const [invitePortalUser, { loading: invitePortalUserExecuting }] =
    useUser_InvitePortalUserMutation({
      update: (store, { data }) => {
        const userData = readPortalAccountUsersQuery(store);
        if (userData) {
          const users = [...userData.data, data!.data];
          writePortalAccountUsersQuery(store, users);
        }

        showToast({
          message: 'Invite email was sent to new user',
          severity: NotificationSeverity.SUCCESS,
        });
        onExitClick();
      },
      onError: (e) =>
        showAlert({
          message: `Error inviting user: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submitForm = async (formData: any) => {
    const commonInput = {
      ...formData,
      portalId,
    };

    // inviting a new user
    invitePortalUser({
      variables: {
        input: {
          ...commonInput,
        },
      },
    });
  };

  useEffect(() => {
    loadSeatQuantity({
      variables: {
        portalId: portalId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubmitButtonDisabled =
    activeUsers.length >= seatQuantity || isSubmitting || invitePortalUserExecuting;

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Invite a New User
      </Typography>
      <Typography>Available Seats: {seatQuantity - activeUsers.length}</Typography>
      <Typography>Used Seats: {activeUsers.length}</Typography>
      <form id="invite-user-form" className={classes.form} onSubmit={handleSubmit(submitForm)}>
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    ref={ref}
                    errorMessage={errors.firstName?.message}
                    label="First Name"
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    ref={ref}
                    errorMessage={errors.lastName?.message}
                    label="Last Name"
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="email"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    ref={ref}
                    errorMessage={errors.email?.message}
                    label="Email"
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Typography variant="caption">Phone Number</Typography>
              <Typography variant="caption" className={classes.warning}>
                Do not use a land-line. Must be a number that can receive text messages for security
                verification purposes.
              </Typography>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field: { name, onBlur, onChange, value } }) => (
                  <FormPhoneField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.phone?.message}
                  />
                )}
              />
              {errors.phone?.message && <FormInputErrorMessage error={errors.phone?.message} />}
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <FormControl>
                <Controller
                  name="portalPermission"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormSelectField
                      {...field}
                      inputRef={ref}
                      options={[
                        { label: 'Admin', value: PortalPermissions.Admin },
                        {
                          label: 'User',
                          value: PortalPermissions.User,
                        },
                      ]}
                      label="Permission"
                      errorMessage={errors.portalPermission?.message}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={8}>
              <Button
                disabled={isSubmitButtonDisabled}
                type="submit"
                form="invite-user-form"
                variant="contained"
                color="primary"
                style={{ marginLeft: '10px' }}
              >
                Invite
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
